import { createTheme } from '@material-ui/core/styles';
import { MODULE_COLORS } from '../../../common/moduleTheme';

const SHORELINE_GREEN = MODULE_COLORS.shoreline;
const PETROL_DARK = '#003832';
const PETROL_MID = '#00504A';

/**
 * @function modePalette
 * @description The surfaces and text colours of a theme mode, shared by the result pages
 * ({@link createShorelineTheme}) and the rest of the app ({@link createAppTheme}), so the steps,
 * the module selection and the result read as one app in either mode.
 * @param {'light'|'dark'} mode
 * @memberof module:Shoreline
 * @returns {Object} The `type`, `background` and `text` of a Material-UI palette.
 */
const modePalette = (mode) => {
  const isDark = mode === 'dark';
  return {
    type: mode,
    background: {
      default: isDark ? '#0B1E2D' : '#F5F7F8',
      paper: isDark ? '#122B3D' : '#FFFFFF',
    },
    text: isDark
      ? { primary: '#F2F6F7', secondary: '#C7D6DC' }
      : { primary: '#1D2B2A', secondary: '#5B6E6C' },
  };
};

/**
 * @constant THEME_MODE_EXCLUDED_PATHS
 * @description Pages under `/main` that stay in the light theme whatever the mode: the FAQ and the
 * problems page are long documents laid out for a white page.
 * @memberof module:Shoreline
 */
const THEME_MODE_EXCLUDED_PATHS = ['/main/faq', '/main/problems'];

/**
 * @function followsThemeMode
 * @description Whether the page at a path is drawn in the chosen light/dark mode - the module
 * selection, every module's steps and every result page.
 * @param {String} pathname
 * **Called by:
 * @see {MainPage}
 * @see {NavigationBar}
 * @memberof module:Shoreline
 * @returns {Boolean}
 */
export const followsThemeMode = (pathname = '') =>
  pathname.startsWith('/main') && !THEME_MODE_EXCLUDED_PATHS.some((path) => pathname.startsWith(path));

/**
 * @function createAppTheme
 * @description The app's own theme in a light/dark mode, for the pages outside the result pages.
 * The light mode is the root theme untouched, so those pages look as they always did; the dark one
 * keeps the root theme's colours, type and overrides and changes only the surfaces and the text -
 * unlike {@link createShorelineTheme}, which also compacts the result pages' floating panels.
 * @param {'light'|'dark'} mode
 * @param {Object} outerTheme - The root theme, from `src/theme.js`.
 * @memberof module:Shoreline
 * @returns {Object}
 */
export const createAppTheme = (mode, outerTheme) =>
  mode === 'dark'
    ? createTheme({
        typography: outerTheme.typography,
        palette: {
          primary: outerTheme.palette.primary,
          secondary: outerTheme.palette.secondary,
          ...modePalette(mode),
        },
        overrides: outerTheme.overrides,
      })
    : outerTheme;

/**
 * @function createShorelineTheme
 * @description Builds a Material-UI theme scoped to the Shoreline result page only, nested inside
 * the app's root `ThemeProvider` (MUI v4 deep-merges nested themes, a pattern already used
 * elsewhere in this codebase — see `theme.js`'s `smallIconButton`/`mediumIconButton`). Applying it
 * via a `<ThemeProvider>` wrapped only around `Shoreline/result.jsx`'s return value keeps every
 * other module's screen — and the root theme itself — completely unaffected, since MUI's
 * `makeStyles` components read whichever theme is closest in the React tree.
 * Also carries the "otimizar espaço" compacting overrides (denser Card/Button/Paper padding) that
 * apply to Shoreline's floating panels, built from the shared components (`ShapeList`,
 * `AnalysisSteps`, etc.) without editing those components themselves.
 * @param {'light'|'dark'} mode - Which variant to build.
 * @param {Object} outerTheme - The root theme (from `src/theme.js`) to extend/merge with.
 * **Called by:
 * @see {ProcessingPage} - Shoreline's result page, via its own `<ThemeProvider>`.
 * @memberof module:Shoreline
 * @returns {Object} A theme object suitable for `<ThemeProvider theme={...}>`.
 */
export const createShorelineTheme = (mode, outerTheme) => {
  const isDark = mode === 'dark';

  return createTheme({
    typography: outerTheme.typography,
    palette: {
      ...modePalette(mode),
      primary: { main: SHORELINE_GREEN, dark: PETROL_DARK },
    },
    overrides: {
      MuiPaper: {
        rounded: { borderRadius: 10 },
        elevation1: { boxShadow: isDark ? '0 2px 10px rgba(0,0,0,0.4)' : '0 2px 10px rgba(0,0,0,0.08)' },
        elevation3: { boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.45)' : '0 4px 16px rgba(0,0,0,0.1)' },
      },
      MuiCard: {
        root: { borderRadius: 10 },
      },
      MuiButton: {
        root: { borderRadius: 8 },
        sizeSmall: { padding: '4px 10px' },
      },
      MuiListItem: {
        root: { paddingTop: 2, paddingBottom: 2 },
      },
      MuiCardContent: {
        root: {
          padding: 12,
          '&:last-child': { paddingBottom: 12 },
        },
      },
      MuiAccordionSummary: {
        root: {
          minHeight: 40,
          paddingLeft: 12,
          paddingRight: 12,
          '&.Mui-expanded': { minHeight: 40 },
        },
        content: {
          marginTop: 8,
          marginBottom: 8,
          '&.Mui-expanded': { marginTop: 8, marginBottom: 8 },
        },
      },
      MuiAccordionDetails: {
        root: { padding: 12 },
      },
      MuiFormControlLabel: {
        root: { marginLeft: -4, marginRight: 0 },
      },
      MuiIconButton: {
        sizeSmall: { padding: 4 },
      },
      MuiTypography: {
        h6: { fontSize: '0.95rem', fontWeight: 700 },
        gutterBottom: { marginBottom: 4 },
      },
      MuiChip: {
        root: { height: 20, fontSize: '0.7rem' },
        sizeSmall: { height: 18 },
      },
    },
  });
};

export const SHORELINE_PETROL_MID = PETROL_MID;

/**
 * @constant OVERLAY_TOP_BAR
 * @description Height, in px, of the app's fixed navigation bar. Every result page's map overlay
 * starts below it, so column heights are measured against the viewport minus this.
 * @memberof module:Shoreline
 */
export const OVERLAY_TOP_BAR = 48;

/**
 * @constant RETURN_BUTTONS_FOOTPRINT
 * @description Height, in px, the floating `ReturnButtons` pair actually occupies: its bottom
 * inset, the two stacked buttons and the gap between them.
 * @memberof module:Shoreline
 */
const RETURN_BUTTONS_FOOTPRINT = 120;

/**
 * @constant RETURN_BUTTONS_CLEARANCE
 * @description Gap, in px, left between the bottom of an overlay column and the top of the
 * floating `ReturnButtons` pair, so the last card stops visibly clear of them instead of ending
 * flush against their top edge.
 * @memberof module:Shoreline
 */
export const RETURN_BUTTONS_CLEARANCE = 30;

/**
 * @constant RETURN_BUTTONS_SAFE_AREA
 * @description Height, in px, reserved at the bottom of the map overlay for the floating
 * `ReturnButtons` pair (two stacked buttons plus their gap and bottom inset). Overlay columns
 * stop short by this much so a long panel scrolls out of view *before* it reaches the buttons,
 * rather than sliding underneath them - the buttons sit above the columns in z-order, so without
 * the reserve the panel content is simply hidden behind them with no way to reach it.
 * @see {@link RETURN_BUTTONS_FOOTPRINT}
 * @see {@link RETURN_BUTTONS_CLEARANCE}
 * @see {@link ReturnButtons}
 * @memberof module:Shoreline
 */
export const RETURN_BUTTONS_SAFE_AREA = RETURN_BUTTONS_FOOTPRINT + RETURN_BUTTONS_CLEARANCE;

/**
 * @constant OVERLAY_BOTTOM_SAFE_AREA_VARIABLE
 * @description Name of the CSS custom property `ReturnButtons` writes its measured reserve into,
 * on the page wrapper. Columns read it through {@link OVERLAY_BOTTOM_SAFE_AREA}.
 * @see {@link ReturnButtons}
 * @memberof module:Shoreline
 */
export const OVERLAY_BOTTOM_SAFE_AREA_VARIABLE = '--overlay-bottom-safe-area';

/**
 * @constant OVERLAY_BOTTOM_SAFE_AREA
 * @description The bottom reserve a left-hand column should subtract, as a CSS value: the height
 * `ReturnButtons` measured for itself at the current window size and font size, falling back to
 * {@link RETURN_BUTTONS_SAFE_AREA} before the first measurement lands (and on a browser with no
 * `ResizeObserver`). Prefer this over the constant: the constant assumes the column and the
 * buttons are laid out against the same box, which is not true on every module, and that the pair
 * is exactly as tall as it is in Portuguese at the default font size.
 * @memberof module:Shoreline
 */
export const OVERLAY_BOTTOM_SAFE_AREA = `var(${OVERLAY_BOTTOM_SAFE_AREA_VARIABLE}, ${RETURN_BUTTONS_SAFE_AREA}px)`;

/**
 * @constant MAP_CONTROLS_SAFE_AREA
 * @description Height, in px, reserved at the bottom of the map overlay for Google's own controls
 * in the bottom-right corner (zoom, Street View, fullscreen). The right-hand column stops this
 * much short of the bottom so its last card scrolls out of view *before* it reaches them, the way
 * {@link RETURN_BUTTONS_SAFE_AREA} does for the bottom-left buttons: the controls sit above the
 * column in z-order, so a card that runs to the bottom edge ends up under them - unreadable, and
 * with the controls themselves harder to hit.
 * @memberof module:Shoreline
 */
export const MAP_CONTROLS_SAFE_AREA = 70;

/**
 * @constant OVERLAY_COLUMN_MAX_HEIGHT
 * @description Max height for an overlay column that does not overlap the return buttons (the
 * right-hand column on every module). Measured against the overlay itself (which already spans
 * only the area below the top bar) rather than the viewport: subtracting the bar height from
 * `100vh` double-counted it, leaving every column a few pixels too tall and showing a
 * full-length scrollbar on columns whose content actually fit.
 * Stops short by {@link MAP_CONTROLS_SAFE_AREA} so the stack clears Google's bottom-right map
 * controls.
 * @memberof module:Shoreline
 */
export const OVERLAY_COLUMN_MAX_HEIGHT = `calc(100% - ${MAP_CONTROLS_SAFE_AREA}px)`;

/**
 * @constant OVERLAY_COLUMN_MAX_HEIGHT_SAFE
 * @description Max height for an overlay column that shares the bottom-left corner with the
 * floating `ReturnButtons` (the left-hand column on every module). Relative to the overlay, for
 * the same reason as {@link OVERLAY_COLUMN_MAX_HEIGHT}.
 * Subtracts the reserve `ReturnButtons` measured for itself rather than a fixed number - see
 * {@link OVERLAY_BOTTOM_SAFE_AREA}.
 * @memberof module:Shoreline
 */
export const OVERLAY_COLUMN_MAX_HEIGHT_SAFE = `calc(100% - ${OVERLAY_BOTTOM_SAFE_AREA})`;

/**
 * @constant OVERLAY_SIDE_GUTTER
 * @description Horizontal inset, in px, between an overlay column and the edge of the map, so the
 * panels (and the "Mapa de fundo" switcher at the top of the right column) never sit flush
 * against the viewport edge.
 * @memberof module:Shoreline
 */
export const OVERLAY_SIDE_GUTTER = 16;

/**
/**
 * @constant OVERLAY_PANEL_WIDTH
 * @description Content width, in px, of an overlay column - and therefore of every card inside it.
 * Cards stretch to it rather than declaring their own widths (they used to each pick one: 260,
 * 300, 350, 320, or none at all), which is what keeps the stack's edges flush and lets the
 * column's scrollbar sit tight against them. The floating return buttons are given this width
 * explicitly, since they sit outside the column.
 * Wide enough for the image chooser, the widest card any column holds - when the column was
 * narrower that card overflowed past the column's left edge and was clipped.
 * @memberof module:Shoreline
 */
export const OVERLAY_PANEL_WIDTH = 320;

/**
 * @constant OVERLAY_SCROLLBAR_GUTTER
 * @description Space, in px, between a panel's right edge and the column's scrollbar.
 * @memberof module:Shoreline
 */
export const OVERLAY_SCROLLBAR_GUTTER = 8;

/**
 * @constant OVERLAY_PANEL_GAP
 * @description Vertical gap, in px, between stacked cards in an overlay column. Owned by the
 * container rather than by each card's own `marginBottom`, so a card nested one level down (a step
 * component that emits two cards, say) sits at the same distance from its neighbour as a card the
 * column holds directly - previously those nested ones fell outside the column's `gap` and ended
 * up flush against each other.
 * @memberof module:Shoreline
 */
export const OVERLAY_PANEL_GAP = 8;

/**
 * @constant OVERLAY_COLUMN_PADDING_TOP
 * @description Top inset, in px, of an overlay column. Shared by both columns of a module so the
 * cards - and the collapse toggle that heads each stack - start on the same horizontal line; the
 * right-hand column used to omit it entirely on several modules, which left its toggle sitting
 * higher than the left one.
 * @memberof module:Shoreline
 */
export const OVERLAY_COLUMN_PADDING_TOP = 12;

/**
 * @constant OVERLAY_MAIN_COLUMN_WIDTH
 * @description Width, in px, of the left-hand overlay column: its side inset, one panel, and just
 * enough room for the scrollbar. Set explicitly instead of through Material-UI's `xs` breakpoint
 * fractions - a `xs={9}` column is three quarters of the viewport while the panels inside it are
 * ~300px, so its scrollbar rendered hundreds of pixels away from them, floating over the middle of
 * the map. Note that `width` alone does not do it: on a flex item the `flex-basis` percentage that
 * `xs` sets wins, so the column must also be given `flexBasis: 'auto'`.
 * @memberof module:Shoreline
 */
export const OVERLAY_MAIN_COLUMN_WIDTH = OVERLAY_SIDE_GUTTER + OVERLAY_PANEL_WIDTH + OVERLAY_SCROLLBAR_GUTTER;

/**
 * @constant OVERLAY_COLUMN_LAYOUT
 * @description Style fragment shared by every overlay column: stretches its cards to the column's
 * content width so they all end up identical, and refuses a horizontal scrollbar. A card wider
 * than the column used to overflow past its edge - on a right-hand column, which aligns its
 * children to the end, it overflowed to the *left* and was clipped there.
 * `minHeight: 0` is what actually lets the column scroll: a flex or grid item defaults to
 * `min-height: auto`, which resolves to its content's height and, being a minimum, overrides the
 * `maxHeight` cap - so the column grew past the viewport and its contents were cut off instead.
 * Spread this alongside the column's own width, padding and `maxHeight`.
 * @memberof module:Shoreline
 */
export const OVERLAY_COLUMN_LAYOUT = {
  alignItems: 'stretch',
  overflowX: 'hidden',
  minHeight: 0,
};

/**
 * @constant OVERLAY_SIDE_COLUMN_WIDTH
 * @description Width, in px, of the right-hand overlay column, for the same reason as
 * {@link OVERLAY_MAIN_COLUMN_WIDTH}. Fits the widest card it holds (the image chooser).
 * @memberof module:Shoreline
 */
export const OVERLAY_SIDE_COLUMN_WIDTH = OVERLAY_MAIN_COLUMN_WIDTH;

/**
 * @function fitBoundsWithOverlayPadding
 * @description Zooms and pans a live Google Map so the loaded area fills the part of the map that
 * is actually visible - the overlay columns cover roughly 450px on the left and 300px on the
 * right, so a plain `fitBounds` centres the region under the panels. Passing per-side padding
 * keeps the whole area in the clear strip between them.
 * The padding is scaled down on narrow viewports: Google Maps refuses to zoom (or snaps to the
 * minimum zoom) when the requested padding approaches the size of the map div, so the insets are
 * clamped to a fraction of the actual map size rather than hardcoded.
 * @param {Object} map - A live `google.maps.Map` instance.
 * @param {Object} bounds - A `google.maps.LatLngBounds` covering the area to frame.
 * **Called by:
 * @see {ProcessingPage} - each module result page, right after drawing its ROI.
 * @memberof module:Shoreline
 * @returns {void}
 */
export const fitBoundsWithOverlayPadding = (map, bounds) => {
  if (!map || !bounds) return;

  const div = typeof map.getDiv === 'function' ? map.getDiv() : null;
  const width = div ? div.offsetWidth : 0;
  const height = div ? div.offsetHeight : 0;
  const base = { top: 80, right: 300, bottom: 140, left: 450 };

  if (!width || !height) {
    map.fitBounds(bounds);
    return;
  }

  const horizontal = base.left + base.right;
  const vertical = base.top + base.bottom;
  const scaleX = horizontal > width * 0.7 ? (width * 0.7) / horizontal : 1;
  const scaleY = vertical > height * 0.6 ? (height * 0.6) / vertical : 1;

  map.fitBounds(bounds, {
    top: Math.round(base.top * scaleY),
    right: Math.round(base.right * scaleX),
    bottom: Math.round(base.bottom * scaleY),
    left: Math.round(base.left * scaleX),
  });
};

/**
 * @function fitCoordinatesWithOverlayPadding
 * @description Frames a map on an area given as coordinates, in the strip left clear by the
 * overlay panels.
 * Takes the coordinates rather than a ready `LatLngBounds` on purpose. The pages keep their bounds
 * in a ref that only the map's load callback fills, so a caller running before that - or after a
 * session import, where the area arrives on its own schedule - got an empty ref and silently
 * framed nothing, leaving the map on its default world view with the analysis a speck in the
 * middle. The coordinates are always there.
 * @param {Object} map - The Google Maps instance.
 * @param {Array<Array<Number>>} coordinates - The area, as `[lng, lat]` pairs.
 * **Called by:
 * @see {ProcessingPage} - before a PDF report captures the screen.
 * @memberof module:Shoreline
 * @returns {Boolean} Whether there was an area to frame.
 */
export const fitCoordinatesWithOverlayPadding = (map, coordinates) => {
  if (!map || !coordinates?.length) return false;
  const bounds = new window.google.maps.LatLngBounds();
  coordinates.forEach(([lng, lat]) => {
    if (Number.isFinite(Number(lng)) && Number.isFinite(Number(lat))) bounds.extend({ lat, lng });
  });
  if (bounds.isEmpty()) return false;
  fitBoundsWithOverlayPadding(map, bounds);
  return true;
};

export const SHORELINE_LIGHT_MAP_STYLES = [
  { featureType: 'poi.business', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

/**
 * @constant SHORELINE_DARK_MAP_STYLES
 * @description Google Maps Styling array for a dark ("night mode") roadmap base layer — only
 * applied when the base layer is `roadmap`/`terrain`; the `satellite`/`hybrid` layers never get a
 * `styles` array, so real imagery colors are never altered (per the "no artificial color changes
 * to satellite/geographic data" requirement).
 * @memberof module:Shoreline
 */
/**
 * @constant SHORELINE_HIDE_LABELS_STYLES
 * @description Google Maps Styling array that hides every label (place names, road numbers, POIs)
 * on the roadmap/terrain base layers. Google's own base-layer vocabulary has no "roadmap without
 * labels" map type — `roadmap`/`terrain` always carry labels, and only the imagery pair exposes
 * the distinction natively (`satellite` = no labels, `hybrid` = labels). So the "Marcadores"
 * toggle can only be honoured on the roadmap side through a style override, which is what this
 * array is for. Appended *after* the theme styles so its `visibility: off` wins over the dark
 * theme's own `labels.text.fill` colour rules.
 * @memberof module:Shoreline
 */
export const SHORELINE_HIDE_LABELS_STYLES = [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }];

/**
 * @function buildMapStyles
 * @description Single source of truth for the `styles` array handed to a live Google Maps
 * instance on a module result page, replacing the copy of the same ternary that used to sit in
 * each of the eight result pages. Folds together the two independent inputs that decide it:
 * the light/dark theme mode and the "Marcadores" toggle. Imagery layers (`satellite`/`hybrid`)
 * never receive the dark palette, so real satellite colours are never altered, and they never
 * receive the label override either since they express labels through the base layer itself.
 * @param {Object} params
 * @param {'light'|'dark'} params.themeMode - The module's current theme mode.
 * @param {String} params.mapType - The live map's `mapTypeId`.
 * @param {Boolean} [params.labelsVisible=true] - The "Marcadores" toggle, roadmap side only.
 * **Called by:
 * @see {ProcessingPage} - every module result page, on theme-mode / map-type / labels change.
 * @memberof module:Shoreline
 * @returns {Array} A Google Maps Styling array.
 */
export const buildMapStyles = ({ themeMode, mapType, labelsVisible = true }) => {
  const isImagery = mapType === 'satellite' || mapType === 'hybrid';
  if (isImagery) return SHORELINE_LIGHT_MAP_STYLES;

  const base = themeMode === 'dark' ? SHORELINE_DARK_MAP_STYLES : SHORELINE_LIGHT_MAP_STYLES;
  return labelsVisible ? base : [...base, ...SHORELINE_HIDE_LABELS_STYLES];
};
export const SHORELINE_DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#0B1E2D' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#0B1E2D' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8CA3AC' }] },
  { featureType: 'poi.business', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6b8a94' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#0f2a22' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#17323f' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#0B1E2D' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8CA3AC' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#1e3f4d' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#17323f' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#061620' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#4c6b78' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#334e56' }] },
];
